import * as React from "react";
import { observer } from "mobx-react";

import { UitkLayoutFlex } from "uitk-react-layout-flex";
import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { UitkSpacing } from "uitk-react-spacing";

import { Experiment, ExperimentControl, ExperimentVariant } from "@shared-ui/experiment-context";

import { FlightAddHotelCheckbox } from "components/flexComponents/WizardFlightPWA/components/FlightAddHotelCheckbox";
import { Location } from "components/shared/Location/Location";
import { Dates } from "components/shared/Dates/Dates";
import { FlightSubLOBProps } from "components/flexComponents/WizardFlightPWA/typings";
import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";

import { FlightsFareCalendar } from "./FlightsFareCalendar";
import { FlightPreferences } from "components/flexComponents/WizardFlightPWA/components/FlightPreferences";
import { Columns } from "components/shared/StorefrontWizard/typings";
import { FlightNonStopCheckbox } from "components/flexComponents/WizardFlightPWA/components/FlightNonStopCheckbox";

export const OnewayFlight = observer((props: FlightSubLOBProps) => {
  const { halfWizard } = props.wizardState.globalWizardState.config;

  const locationColumns: Columns = {
    fullWidth: { small: 2, medium: 8 },
    half: {
      small: 12,
    },
  };

  const datesColumns: Columns = {
    fullWidth: { small: 2, medium: 4 },
    half: {
      small: 12,
    },
  };

  const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, medium: 12 };
  const locationColSpan: UitkLayoutConditionalGridSpan = locationColumns[halfWizard ? "half" : "fullWidth"];
  const datesColSpan: UitkLayoutConditionalGridSpan = datesColumns[halfWizard ? "half" : "fullWidth"];
  const datesColSpanFH: UitkLayoutConditionalGridSpan = datesColumns[halfWizard ? "half" : "fullWidth"];
  const fareCalendarTest = "PWA_fare_calendar_home_page";
  const { enableAddHotel, enableNonStopFlight } = props.lobState.config;

  const {
    errorInputRef,
    isOnewayFormValid,
    numberOfErrors,
    moreThanOneError,
    updateInvalidFormsState,
  } = props.lobState;

  if (isOnewayFormValid) {
    updateInvalidFormsState();
  }

  return (
    <UitkSpacing padding={{ block: "three" }}>
      <UitkLayoutGrid columns={mainGridColumns} space="three">
        {!isOnewayFormValid && (
          <LobErrorSummary
            locHeadingToken={props.getLocError(false)!}
            locHeadingArgs={moreThanOneError() && [numberOfErrors]}
            locLinkToken={props.getLocError(true)}
            linkClickFocusId="roundtrip-error"
            inputErrorRef={errorInputRef}
            colSpan={mainGridColumns}
            setInputsRefs={props.setInputsRefs}
          />
        )}
        {halfWizard && <FlightPreferences {...props} />}
        <Location
          setInputsRefs={props.setInputsRefs}
          originField={true}
          destinationField={true}
          lobState={props.lobState}
          showSwapLocationsControl={true}
          colSpan={locationColSpan}
          globalConfig={props.wizardState.globalWizardState.config}
        />
        <Experiment name={fareCalendarTest}>
          <ExperimentControl>
            <Dates
              singleDate={true}
              lobState={props.lobState}
              colSpan={datesColSpan}
              setInputsRefs={props.setInputsRefs}
            />
          </ExperimentControl>
          <ExperimentVariant bucket={1}>
            <FlightsFareCalendar
              singleDate={true}
              lobState={props.lobState}
              colSpan={datesColSpan}
              setInputsRefs={props.setInputsRefs}
            />
          </ExperimentVariant>
        </Experiment>
        {(enableAddHotel || enableNonStopFlight) && (
          <UitkLayoutGridItem colSpan="all">
            <UitkLayoutFlex space="three" className="all-t-padding-two">
              {enableAddHotel && <FlightAddHotelCheckbox flightWizardState={props.lobState} />}
              {enableNonStopFlight && <FlightNonStopCheckbox flightWizardState={props.lobState} />}
            </UitkLayoutFlex>
          </UitkLayoutGridItem>
        )}
        {props.lobState.config.enableAddHotel && props.lobState.isAddAHotelChecked && (
          <Dates
            lobState={props.lobState}
            isPartialStay={true}
            colSpan={datesColSpanFH}
            setInputsRefs={props.setInputsRefs}
          />
        )}
      </UitkLayoutGrid>
    </UitkSpacing>
  );
});
